/**
 * Navigation for a just-created team review.
 *
 * Why this module exists (dead-click incident 2026-08-03): POST /live-review
 * can take 60-100s in prod (proxy fetch + login-wall preflight), so by the
 * time the create resolves the browser's transient user activation is gone
 * and a bare window.open() is popup-blocked. It returns null, nothing opens,
 * and the modal used to close as if it had worked. The blocked case now
 * falls back to same-tab navigation, and a review that resolves after the
 * user already closed the modal surfaces as a toast instead of a surprise
 * tab or a hijacked navigation.
 *
 * Injected-deps module (house pattern: onboardingFinish.ts) so tests never
 * touch window.open / window.location.
 */
import { toast } from 'sonner';

export type OpenCreatedReviewOutcome = 'opened' | 'navigated' | 'notified';

export interface OpenCreatedReviewDeps {
  openTab?: (url: string) => Window | null;
  navigateTo?: (url: string) => void;
  notify?: (options: { message: string; actionLabel: string; onAction: () => void }) => void;
}

const defaultOpenTab = (url: string): Window | null => window.open(url, '_blank');
const defaultNavigateTo = (url: string): void => window.location.assign(url);
const defaultNotify: NonNullable<OpenCreatedReviewDeps['notify']> = ({ message, actionLabel, onAction }) => {
  toast.success(message, {
    action: { label: actionLabel, onClick: onAction },
    duration: 12000,
  });
};

export function openCreatedReview(
  fullUrl: string,
  closedByUser: boolean,
  deps: OpenCreatedReviewDeps = {},
): OpenCreatedReviewOutcome {
  const openTab = deps.openTab ?? defaultOpenTab;
  const navigateTo = deps.navigateTo ?? defaultNavigateTo;
  const notify = deps.notify ?? defaultNotify;

  if (closedByUser) {
    // The user deliberately left; a toast keeps the outcome visible without
    // stealing their tab. The action click carries fresh user activation, so
    // openTab works there; if a blocker still refuses, navigate in place.
    notify({
      message: 'Your team review is ready.',
      actionLabel: 'Open review',
      onAction: () => {
        if (!openTab(fullUrl)) navigateTo(fullUrl);
      },
    });
    return 'notified';
  }

  if (openTab(fullUrl)) return 'opened';
  // Popup blocked (no transient activation left after a long create):
  // navigating the current tab needs no activation and cannot be blocked.
  navigateTo(fullUrl);
  return 'navigated';
}
